import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface ReferralRow {
  row_key: string
  customer_id: number
  contact_id: number
  company: string | null
  contact_name: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  last_referral: string | null
  num_referrals: number
  referral_dollars: number
}

interface SelectOption {
  value: string | number
  label: string
}

interface Props {
  results: ReferralRow[]
  total_count: number
  since_date: string
  search_description: string
  customer_types: SelectOption[]
  states: SelectOption[]
  selected_type_id: string
  selected_state: string
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined || amount === 0) return ""
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* -- Columns ---------------------------------------------- */

const PER_PAGE = 25

const columns: HpiColumn<ReferralRow>[] = [
  {
    header: "Firm",
    render: (row) => (
      <a
        href={`/customers/${row.customer_id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.company ?? "-"}
      </a>
    ),
  },
  {
    header: "Name",
    render: (row) => (
      <a
        href={`/customers/${row.customer_id}/contacts/${row.contact_id}/edit`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.contact_name ?? "-"}
      </a>
    ),
  },
  {
    header: "Address",
    render: (row) => <span className="text-sm">{row.address ?? ""}</span>,
  },
  {
    header: "Suite/Building",
    width: "max-w-[140px]",
    render: (row) => <span className="text-sm">{row.address2 ?? ""}</span>,
  },
  {
    header: "City",
    width: "max-w-[120px]",
    render: (row) => <span className="text-sm">{row.city ?? ""}</span>,
  },
  {
    header: "State",
    width: "max-w-[60px]",
    render: (row) => <span className="text-sm">{row.state ?? ""}</span>,
  },
  {
    header: "Zip",
    width: "max-w-[80px]",
    render: (row) => <span className="text-sm">{row.zip ?? ""}</span>,
  },
  {
    header: "Last Referral",
    width: "max-w-[110px]",
    render: (row) => (
      <span className="text-sm">{formatDate(row.last_referral)}</span>
    ),
  },
  {
    header: "# Referrals",
    width: "max-w-[90px]",
    render: (row) => (
      <span className="block text-right text-sm">{row.num_referrals}</span>
    ),
  },
  {
    header: "Ref $s",
    width: "max-w-[110px]",
    render: (row) => (
      <span className="block text-right text-sm">
        {formatCurrency(row.referral_dollars)}
      </span>
    ),
  },
]

/* -- Main Component --------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/case_activity" },
  { title: "Referral Mailing", href: "/reports/marketing/referral_mailing" },
]

export default function ReferralMailingReport({
  results,
  total_count,
  since_date,
  search_description,
  customer_types,
  states,
  selected_type_id,
  selected_state,
}: Props) {
  const [typeId, setTypeId] = useState(selected_type_id)
  const [stateFilter, setStateFilter] = useState(selected_state)
  const [sinceDate, setSinceDate] = useState(since_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(results.length / PER_PAGE)
  const paginated = results.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/marketing/referral_mailing",
      {
        type_id: typeId || undefined,
        state: stateFilter || undefined,
        since: sinceDate || undefined,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Referral Mailing List" />

      <div className="p-6">
        {/* Filter Bar */}
        <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
          <HpiFilterSelect
            label="Type"
            value={typeId}
            onChange={setTypeId}
            options={customer_types}
          />
          <HpiFilterSelect
            label="State"
            value={stateFilter}
            onChange={setStateFilter}
            options={states}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Referred Since
            </label>
            <input
              type="date"
              value={sinceDate}
              onChange={(e) => setSinceDate(e.target.value)}
              className="h-8 rounded border border-gray-300 px-2 text-sm"
            />
          </div>
          <button
            onClick={handleUpdate}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
          >
            Search
          </button>
        </div>

        {/* Data Table */}
        <HpiDataTable<ReferralRow>
          title={`Referrals  ${search_description}`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.row_key}
          pagination={
            results.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={results.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Records",
              value: total_count,
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
